import React from 'react';
import { ApplicationView, useController } from '@aha-app/mvc';
import styles from './frameMenuStyles.module.css';
import WhiteboardController from '../controllers/WhiteboardController';
import ToolButton from '../toolbars/ToolButton';
import {
  generateFrameMenuStructure,
  type FrameMenuStylus,
  type FrameMenuSection,
} from '../utils/frameMenu';
import phone from '../assets/icons/devicePhoneMenuIcon.svg';
import tablet from '../assets/icons/deviceTabletMenuIcon.svg';
import browser from '../assets/icons/deviceBrowserMenuIcon.svg';
import laptop from '../assets/icons/deviceLaptopMenuIcon.svg';
import stack from '../assets/icons/componentStackMenuIcon.svg';
import swimlane from '../assets/icons/componentSwimlaneMenuIcon.svg';
import board from '../assets/icons/componentBoardMenuIcon.svg';
import { svgToIcon } from '../utils/svg';

const DEVICE_ICONS = {
  phone,
  tablet,
  browser,
  laptop,
};

const COMPONENT_ICONS = {
  stack,
  swimlane,
  board,
};

// Workaround for icons in this menu. This saves us from having to create a copy
// of the stylus just to override renderIcon
const getIcon = stylus => {
  if (stylus.deviceName) {
    return svgToIcon(DEVICE_ICONS[stylus.deviceName]);
  }

  if (stylus.componentName) {
    return svgToIcon(COMPONENT_ICONS[stylus.componentName]);
  }

  if (stylus.ratio) {
    return <span style={{ fontSize: '20px' }}>{stylus.renderIcon()}</span>;
  }

  return stylus.renderIcon();
};

const FrameMenu: React.FC = () => {
  const controller = useController(WhiteboardController);
  const { shapeDragManager } = controller;
  const sections = React.useMemo(
    () => generateFrameMenuStructure(controller),
    [controller]
  );

  const renderSection = (section: FrameMenuSection<FrameMenuStylus>) => {
    return (
      <div key={section.title ?? 'default'} className={styles.frameMenuSection}>
        {section.title && (
          <div className={styles.frameMenuSectionHeading}>{section.title}</div>
        )}
        <div className={styles.frameMenuSectionContent}>
          {section.items.map(stylus => {
            return (
              <ToolButton
                key={stylus.stylusId()}
                className={styles.frameMenuButton}
                draggable={true}
                onDragStart={e => shapeDragManager.startDrag(e, stylus)}
                onClick={event => {
                  // Use actionSetFrameStylus for frame-related styluses to enable memory
                  // Check for frame-related styluses by their properties
                  const isFrameStylus =
                    'ratio' in stylus ||
                    'deviceName' in stylus ||
                    'componentName' in stylus;
                  if (isFrameStylus) {
                    controller.actionSetFrameStylus(stylus);
                  } else {
                    controller.actionSetStylus(stylus);
                  }
                  controller.actionSetStylusMenu(null);
                  event.stopPropagation();
                }}
              >
                {getIcon(stylus)}
                <span className={styles.frameMenuButtonLabel}>
                  {stylus.description()}
                </span>
              </ToolButton>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className={styles.frameMenu} data-drag-abort>
      <div className={styles.frameMenuContent}>
        {sections.map(renderSection)}
      </div>
    </div>
  );
};

export default ApplicationView(FrameMenu);
